import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)`
        }}></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Content */}
          <div className="lg:col-span-7 space-y-8">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600 transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={16} />
              <Link to="/blog-hub" className="hover:text-blue-600 transition-colors">Blog</Link>
              <Icon name="ChevronRight" size={16} />
              <span className="text-gray-900 font-medium">Développement des Compétences</span>
            </nav>

            {/* Article Meta */}
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} />
                <span>6 Novembre 2025</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>12 min de lecture</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="User" size={16} />
                <span>Équipe CRF PERFECTION</span>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Développement des Compétences à l'Ère Numérique
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Une exploration stratégique de la transformation digitale qui redéfinit le développement professionnel 
                et l'acquisition de compétences dans l'industrie automobile moderne.
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Intelligence Artificielle
              </span>
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Développement des Compétences
              </span>
              <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Transformation Digitale
              </span>
              <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                Formation Professionnelle
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl">
                <Icon name="Play" size={20} />
                <span>Évaluation Gratuite</span>
              </button>
              <button className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-4 rounded-lg font-semibold border-2 border-gray-200 transition-all duration-200">
                <Icon name="Download" size={20} />
                <span>Guide PDF</span>
              </button>
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-xl p-6 space-y-6">
                  {/* Skills Visualization */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-900">Compétences Clés 2025</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Intelligence Artificielle</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full" style={{width: '85%'}}></div>
                        </div>
                        <span className="text-sm font-bold text-blue-600">85%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Transformation Digitale</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{width: '78%'}}></div>
                        </div>
                        <span className="text-sm font-bold text-green-600">78%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Leadership Numérique</span>
                        <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2">
                          <div className="bg-purple-500 h-2 rounded-full" style={{width: '72%'}}></div>
                        </div>
                        <span className="text-sm font-bold text-purple-600">72%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">95%</div>
                      <div className="text-xs text-gray-600">Taux de Réussite</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">2.3x</div>
                      <div className="text-xs text-gray-600">Amélioration Performance</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                <Icon name="Zap" size={32} className="text-white" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                <Icon name="Target" size={24} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Navigation Rapide</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <a href="#assessment" className="flex items-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200">
              <Icon name="CheckCircle" size={20} className="text-blue-600" />
              <span className="font-medium text-gray-900">Évaluation</span>
            </a>
            <a href="#framework" className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200">
              <Icon name="Layers" size={20} className="text-green-600" />
              <span className="font-medium text-gray-900">Référentiel</span>
            </a>
            <a href="#programs" className="flex items-center space-x-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200">
              <Icon name="BookOpen" size={20} className="text-purple-600" />
              <span className="font-medium text-gray-900">Programmes</span>
            </a>
            <a href="#success" className="flex items-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors duration-200">
              <Icon name="Award" size={20} className="text-orange-600" />
              <span className="font-medium text-gray-900">Témoignages</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;